import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

/**
 * NarrativeChoiceGate — Light beige break between the diagnostic (dark dossier) and the offers (dark tension gates).
 *
 * Purpose: create a clear VISUAL and NARRATIVE rupture. The reader pauses, internalizes "I have the truth, now I have to choose".
 *
 * Props:
 *  - mode: 'standard' | 'ouverture'  (changes columns content)
 *  - firstName: optional, for soft personalization
 */
const NarrativeChoiceGate = ({ mode = 'standard', firstName }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-120px' });
  const navigate = useNavigate();

  const isOuv = mode === 'ouverture';

  // Where each card should navigate — carte offres sur la page de vente
  const goToSabrilExpress = () => {
    navigate('/prsnl#offres');
    setTimeout(() => {
      const el = document.getElementById('offres');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
  };
  const rightHref = '/configurer';     // wizard 4 étapes

  const headline = isOuv
    ? ['VOUS CONNAISSEZ LES RISQUES.', 'À VOUS DE DÉCIDER.']
    : ['VOUS AVEZ LE DIAGNOSTIC.', 'À VOUS DE CHOISIR.'];

  const subline = isOuv
    ? "Une ouverture, ça se prépare avec quelqu'un qui en a fait. Sabril cadre votre projet de A à Z — concept, marque, équipe, jour J."
    : "Vous pouvez démarrer par un cadrage rapide en 2 visios, ou configurer directement votre accompagnement 90 jours avec Sabril.";

  const left = isOuv
    ? null  // pas de format court en mode ouverture
    : {
        tag: '⚡ Option 1 · Sabril Express',
        title: 'DEUX VISIOS + PLAN',
        price: '790€ HT',
        bullets: ['2 visios de 45 min avec Sabril', 'Plan d\'action PDF personnalisé', 'WhatsApp direct 30 jours'],
      };

  const right = isOuv
    ? {
        tag: '🚀 Salon Privé · Ouverture',
        title: 'AVEC SABRIL · DE A À Z',
        price: 'Sur validation',
        bullets: ['Ouverture de A à Z · 3 mois', 'Sabril sur place, terrain', 'Concept · marque · équipe · marketing'],
      }
    : {
        tag: '🤝 Option 2 · Salon Privé',
        title: 'AVEC SABRIL · 90 JOURS',
        price: 'Sur configuration',
        bullets: ['3 formats (distance / 48h / hybride)', '12 chantiers à la carte', 'Marque · ops · équipe · résultats'],
      };

  return (
    <section
      ref={ref}
      className="relative my-10 sm:my-14 -mx-4 sm:-mx-5"
      data-testid="narrative-choice-gate"
    >
      {/* Light beige strip — full width to break the dark cards continuity above */}
      <div
        className="relative px-6 sm:px-10 py-12 sm:py-16"
        style={{ background: 'linear-gradient(180deg, #f5f3f0 0%, #efece6 100%)' }}
      >
        {/* Top accent line — visual "cut" mark */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-1 bg-[#1a1a1a]/15" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-[#F05A1A]" />

        <div className="max-w-2xl mx-auto text-center">
          {/* Kicker */}
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-[10px] sm:text-xs font-black tracking-[4px] uppercase text-[#1a1a1a]/55 mb-3"
          >
            ↓ Étape 3 · {firstName ? `${firstName}, v` : 'V'}ous choisissez maintenant
          </motion.p>

          {/* Headline — 2 lines, second line orange gradient */}
          <motion.h2
            initial={{ opacity: 0, y: 14 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="font-bebas text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-[#1a1a1a]"
          >
            {headline[0]}
          </motion.h2>
          <motion.h2
            initial={{ opacity: 0, y: 14 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="font-bebas text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight mb-5"
            style={{
              background: 'linear-gradient(135deg, #F05A1A 0%, #FF7A3D 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {headline[1]}
          </motion.h2>

          {/* Subline */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.55 }}
            className="text-[#1a1a1a]/65 text-sm sm:text-base leading-relaxed max-w-md mx-auto mb-8"
          >
            {subline}
          </motion.p>

          {/* 1 or 2 column comparison (single column for ouverture mode) */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.7 }}
            className={`${left ? 'grid grid-cols-2 gap-3 sm:gap-4' : 'flex justify-center'} text-left`}
          >
            {/* Left card — Sabril Express (clickable → /prsnl#offres) */}
            {left && (
            <button
              type="button"
              onClick={goToSabrilExpress}
              className="text-left rounded-2xl p-4 sm:p-5 bg-white border border-[#1a1a1a]/10 shadow-[0_4px_18px_rgba(0,0,0,0.06)] hover:shadow-[0_10px_32px_rgba(0,0,0,0.12)] hover:-translate-y-0.5 transition-all group cursor-pointer w-full"
              data-testid="choice-gate-option-1"
            >
              <p className="text-[10px] font-black tracking-widest uppercase text-cyan-600 mb-2">
                {left.tag}
              </p>
              <h3 className="font-bebas text-2xl sm:text-3xl text-[#1a1a1a] mb-1 leading-tight">{left.title}</h3>
              <p className="text-[11px] font-bold text-[#1a1a1a]/55 mb-3">{left.price}</p>
              <ul className="space-y-1.5">
                {left.bullets.map((b, i) => (
                  <li key={i} className="text-[11px] sm:text-xs text-[#1a1a1a]/70 flex items-start gap-1.5 leading-snug">
                    <span className="text-cyan-600 flex-shrink-0 mt-0.5">·</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-cyan-600 mt-3 group-hover:gap-2.5 transition-all">
                Voir Sabril Express <ArrowRight className="w-3.5 h-3.5" strokeWidth={3} />
              </span>
            </button>
            )}

            {/* Right card — Salon Privé (clickable → /configurer) */}
            <button
              type="button"
              onClick={() => navigate(rightHref)}
              className={`text-left rounded-2xl p-4 sm:p-5 bg-white border-2 border-[#F05A1A]/50 shadow-[0_8px_28px_rgba(240,90,26,0.18)] hover:shadow-[0_16px_42px_rgba(240,90,26,0.32)] hover:-translate-y-0.5 transition-all group cursor-pointer relative ${!left ? 'max-w-md w-full' : 'w-full'}`}
              data-testid="choice-gate-option-2"
            >
              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-[#F05A1A] text-white text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full shadow-md whitespace-nowrap">
                {left ? 'Recommandé' : 'Le bon move'}
              </span>
              <p className="text-[10px] font-black tracking-widest uppercase text-[#F05A1A] mb-2">
                {right.tag}
              </p>
              <h3 className="font-bebas text-2xl sm:text-3xl text-[#1a1a1a] mb-1 leading-tight">{right.title}</h3>
              <p className="text-[11px] font-bold text-[#1a1a1a]/55 mb-3">{right.price}</p>
              <ul className="space-y-1.5">
                {right.bullets.map((b, i) => (
                  <li key={i} className="text-[11px] sm:text-xs text-[#1a1a1a]/70 flex items-start gap-1.5 leading-snug">
                    <span className="text-[#F05A1A] flex-shrink-0 mt-0.5">·</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-[#F05A1A] mt-3 group-hover:gap-2.5 transition-all">
                {isOuv ? 'Voir l\'accompagnement Ouverture' : 'Configurer mon accompagnement'} <ArrowRight className="w-3.5 h-3.5" strokeWidth={3} />
              </span>
            </button>
          </motion.div>

          {/* Soft scroll cue */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 1.1 }}
            className="text-[10px] sm:text-xs font-bold tracking-widest uppercase text-[#1a1a1a]/45 mt-7"
          >
            ↓ {left ? 'Découvrez chaque option en détail' : 'Découvrez l\'accompagnement Ouverture en détail'}
          </motion.p>
        </div>

        {/* Bottom accent line */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-32 h-1 bg-[#1a1a1a]/15" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-3 h-3 rounded-full bg-[#F05A1A]" />
      </div>
    </section>
  );
};

export default NarrativeChoiceGate;
